import React, { useState } from 'react';
import {
  Checkbox,
  Switch,
  FormControlLabel,
  Box,
  Grid,
} from '@material-ui/core';
import { withFormik, FormikProps } from 'formik';
import { IntlShape, injectIntl } from 'react-intl';
import * as Yup from 'yup';
import Modal from 'components/Modal';
import { useSelector } from 'react-redux';

import { translate } from 'helpers/intl';
import messages from 'containers/LoginRecoveryPage/SendLoginPage/messages';
import { Captions, ShortBody, LongBody } from 'components/Typography';
import InputField from 'components/InputField';
import {
  StyledBox,
  GeneralConditions,
  MemoIdBox,
  StyledCaptions,
  StyledSmallSectionHeader,
  SizedLongBody,
  StyledBodyText,
  StyledOrangeButton,
} from 'containers/LoginRecoveryPage/SendLoginPage/styles';
import { sanlamBlue } from 'Material-UI/colors';
import { OrangeButton } from 'components/Button';
import GaClickHOC from 'helpers/GATracking/GaClickHOC';
import { EVENT_CATEGORIES } from 'helpers/GATracking';
import { getLocaleSelector } from 'store/LanguageProvider/selectors';
import { cgu } from 'translations/cgu';

const InnerForm = (
  props: {
    email: string;
    identifiant: string;
  } & FormikProps<FormValues>,
) => {
  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = props;
  const language = useSelector(getLocaleSelector);

  const [open, setOpen] = useState(false);

  const handleOnClickSaveId = () => {
    !values.isSaveUserId
      ? localStorage.setItem('identifiant', values.identifiant)
      : localStorage.setItem('identifiant', '');
  };

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleIsTermsAccepted = () => {
    setFieldValue('isTermsAccepted', true);
    setOpen(false);
  };
  return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <Box mb={3} px={[2, 4, 8]}>
          <StyledSmallSectionHeader color={sanlamBlue} weight="500">
            {values.identifiant}
          </StyledSmallSectionHeader>
          <MemoIdBox marginBottom={2}>
            <GaClickHOC
              category={EVENT_CATEGORIES.FORGOTTEN_ACCESS_RECOVERY_LOGIN}
              label="Mémoriser mon identifiant"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    name="isSaveUserId"
                    value={values.isSaveUserId}
                    onChange={handleChange}
                    onClick={handleOnClickSaveId}
                    checked={values.isSaveUserId}
                    data-testid="is-save-user-id"
                  />
                }
                label={
                  <StyledCaptions>
                    {translate({ ...messages.memoId }, props)}
                  </StyledCaptions>
                }
              />
            </GaClickHOC>
          </MemoIdBox>

          <Box marginBottom={1.25}>
            <ShortBody>
              {translate({ ...messages.iWantToSaveMyId }, props)}
            </ShortBody>
          </Box>
          <StyledBox px={3} py={0.75} isRadiusTop>
            <Grid container justify="space-between" alignItems="center">
              <Grid xs={6} item>
                <LongBody weight="500">
                  {translate({ ...messages.inMyAddressMail }, props)}{' '}
                </LongBody>
              </Grid>
              <Grid xs={2} item>
                <Box marginLeft={4}>
                  <GaClickHOC
                    category={EVENT_CATEGORIES.FORGOTTEN_ACCESS_RECOVERY_LOGIN}
                    label="Sauvegarder par email"
                  >
                    <Switch
                      checked={values.inMyAddressMail}
                      onChange={handleChange}
                      name="inMyAddressMail"
                      color="primary"
                      data-testid="in-my-address-mail"
                    />
                  </GaClickHOC>
                </Box>
              </Grid>
            </Grid>
          </StyledBox>
          <StyledBox px={3} paddingTop={3} paddingBottom={1}>
            <Box marginBottom={3}>
              <InputField
                name="email"
                placeholder={translate({ ...messages.email }, props)}
                label={translate({ ...messages.email }, props)}
                value={values.email}
                error={errors.email && touched.email}
                helperText={touched.email && errors.email}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
            </Box>

            <Grid container direction="row" spacing={3} alignItems="flex-start">
              <Grid xs={1} item>
                <Checkbox
                  color="primary"
                  name="isTermsAccepted"
                  value={values.isTermsAccepted}
                  onChange={handleChange}
                  checked={values.isTermsAccepted}
                  data-testid="is-terms-accepted"
                />
              </Grid>
              <Grid xs={10} item>
                <Box marginTop={1}>
                  <Captions>
                    {translate({ ...messages.iAccept }, props)}
                    <GeneralConditions
                      data-testid="terms-and-conditions"
                      onClick={() => {
                        handleOpenModal();
                      }}
                    >
                      {' '}
                      {translate({ ...messages.termsAndConditions }, props)}
                    </GeneralConditions>
                    {translate({ ...messages.personalData }, props)}
                  </Captions>
                  {errors.isTermsAccepted && touched.isTermsAccepted && (
                    <Captions color={'#F5523C'}>
                      {errors.isTermsAccepted}
                    </Captions>
                  )}
                </Box>
              </Grid>
            </Grid>
          </StyledBox>
          <Box marginBottom={2}></Box>
          <Grid>
            <GaClickHOC
              category={EVENT_CATEGORIES.FORGOTTEN_ACCESS_RECOVERY_LOGIN}
              label="Confirmer"
            >
              <StyledOrangeButton type="submit" variant="contained">
                <React.Fragment>
                  {translate({ ...messages.confirm }, props)}
                </React.Fragment>
              </StyledOrangeButton>
            </GaClickHOC>
          </Grid>
        </Box>
      </form>
      <Modal
        open={open}
        handleOpen={handleOpenModal}
        handleClose={handleCloseModal}
        header={
          <SizedLongBody weight="500" color="#04336B">
            {translate({ ...messages.modalTitle }, props)}
          </SizedLongBody>
        }
        content={<StyledBodyText>{cgu[language]}</StyledBodyText>}
        footer={
          <Grid spacing={5} container justify="flex-end">
            <Grid item>
              {' '}
              <OrangeButton onClick={handleCloseModal}>
                {translate({ ...messages.close }, props)}
              </OrangeButton>
            </Grid>
            <Grid item>
              <OrangeButton variant="contained" onClick={handleIsTermsAccepted}>
                {translate({ ...messages.accept }, props)}
              </OrangeButton>
            </Grid>
          </Grid>
        }
      ></Modal>
    </React.Fragment>
  );
};

export interface FormProps {
  intl: IntlShape;
  email: string;
  onSubmitStep: Function;
  identifiant: string;
}

interface FormValues {
  inMyAddressMail: boolean;
  isSaveUserId: boolean;
  isTermsAccepted: boolean;
  email: string;
  identifiant: string;
}

const IdMemo = withFormik<FormProps, FormValues>({
  mapPropsToValues: (props) => {
    return {
      isSaveUserId: false,
      inMyAddressMail: true,
      isTermsAccepted: false,
      identifiant: props.identifiant,
      email: props.email,
    };
  },

  validationSchema: (props: any) =>
    Yup.object().shape({
      inMyAddressMail: Yup.boolean(),
      email: Yup.string()
        .email(translate({ ...messages.emailInputError }, props))
        .when('inMyAddressMail', {
          is: true,
          then: Yup.string().required(
            translate({ ...messages.requiredField }, props),
          ),
        }),
      isTermsAccepted: Yup.boolean().oneOf(
        [true],
        translate({ ...messages.conditionsGeneralError }, props),
      ),
    }),

  handleSubmit: (values, { props }) => {
    const valuesToSend = {
      login: values.identifiant,
      inMyAddressMail: values.inMyAddressMail,
      email: values.email,
    };
    props.onSubmitStep(valuesToSend);
  },
})(InnerForm);

export default injectIntl(IdMemo);
